import { where } from 'firebase/firestore'
import { CONST } from '../../../../../../../const/const'
import { EventStaffModel } from '../../../../../../../models/event-staff/event-staff.model'
import { HorseCompetitonPaperworkModel } from '../../../../../../../models/horse-competiton-paperwork/horse-competiton-paperwork.model'
import { IRegistrationFeesInterface } from '../../../../../../../models/registration-fees/registrationFees.interface'
import { RegistrationFeesModel } from '../../../../../../../models/registration-fees/registrationFees.model'
import { IRiderTeamMemberInterface } from '../../../../../../../models/rider-team-member/riderTeamMember.interface'
import { RiderTeamMemberModel } from '../../../../../../../models/rider-team-member/riderTeamMember.model'
import FirestoreService from '../../../../../../../services/firestoreService'

/**
 * This service contains data fetching functions specifically for report generation purposes.
 *
 * Architecture note:
 * These functions fetch data that's only needed for specific reports, rather than having
 * the parent component (ManageClinicReportsRoot) fetch this data that is not needed in every child component
 *
 * -- Avoids loading unnecessary data for child components of ManageClinicReportsRoot that do not need it
 * -- Improves initial load time for the parent component (ManageClinicReportsRoot) which is already
 *    fetching lots of data that is needed for all its children components
 */

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

interface PersonnelStats {
  // team member stats
  riders: number
  trainers: number
  owners: number
  guardians: number

  // event staff stats
  showManager: number
  secretary: number
  technicalDelegate: number
  courseDesigners: number
  judges: number
  scorers: number
  stewards: number
  ingates: number
  announcers: number
  farriers: number
  veterinarians: number
  volunteers: number
  press: number
  otherStaff: number

  // total count
  allStaff: number
}

/**
 * Fetches and calculates personnel statistics for an event
 *
 * This function queries the Firestore database to retrieve rider team members
 * and event staff data for a specific event, then calculates aggregate counts
 * categorized by roles.
 *
 * @param {string} eventId - Unique identifier of the event
 * @returns {Promise<PersonnelStats>} A promise that resolves to an object containing personnel counts
 * @throws {Error} If there's a problem fetching data from Firestore
 *
 * @example
 * Get personnel stats for an event
 * const stats = await getPersonnelStats('somePegasusEvent');
 * stats.riders
 */
export const getPersonnelStats = async (eventId: string): Promise<PersonnelStats> => {
  try {
    // get team members (riders, trainers, owners, guardians)
    const teamMembersSnap = await FirestoreService.filterItems(COLLECTIONS.RIDER_TEAM_MEMBER.NAME, [
      where(COLLECTIONS.RIDER_TEAM_MEMBER.FIELDS.EVENT_ID.KEY, '==', eventId),
    ])

    // get staff members
    const staffSnap = await FirestoreService.getItem(COLLECTIONS.EVENT_STAFF.NAME, eventId)

    const stats: PersonnelStats = {
      // team member stats
      riders: 0,
      trainers: 0,
      owners: 0,
      guardians: 0,

      // event staff stats
      showManager: 0,
      secretary: 0,
      technicalDelegate: 0,
      courseDesigners: 0,
      judges: 0,
      scorers: 0,
      stewards: 0,
      ingates: 0,
      announcers: 0,
      farriers: 0,
      veterinarians: 0,
      volunteers: 0,
      press: 0,
      otherStaff: 0,

      // total count
      allStaff: 0,
    }

    // count team members by role
    teamMembersSnap.docs.forEach((doc) => {
      const member = RiderTeamMemberModel.fromFirestoreDoc(doc).toObject()
      switch (member.teamMemberRole) {
        case 'Rider':
          stats.riders++
          break
        case 'Trainer':
          stats.trainers++
          break
        case 'Owner':
          stats.owners++
          break
        case 'Guardian':
          stats.guardians++
          break
      }
    })

    if (staffSnap.exists()) {
      const staff = EventStaffModel.fromFirestoreDoc(staffSnap).toObject()

      if (staff.eventStaff && Array.isArray(staff.eventStaff)) {
        staff.eventStaff.forEach((staffItem) => {
          const title = staffItem.title || ''
          const count = staffItem.value?.length || 0

          stats.allStaff += count

          // map staff title to specific property
          switch (title) {
            case 'Show manager':
              stats.showManager = count
              break
            case 'Secretary':
              stats.secretary = count
              break
            case 'Technical Delegate':
              stats.technicalDelegate = count
              break
            case 'Course Designers':
              stats.courseDesigners = count
              break
            case 'Judges':
              stats.judges = count
              break
            case 'Scorers':
              stats.scorers = count
              break
            case 'Stewards':
              stats.stewards = count
              break
            case 'Ingates':
              stats.ingates = count
              break
            case 'Announcers':
              stats.announcers = count
              break
            case 'Farriers':
              stats.farriers = count
              break
            case 'Veterinarians':
              stats.veterinarians = count
              break
            case 'Volunteers':
              stats.volunteers = count
              break
            case 'Press':
              stats.press = count
              break
            // any other titles not explicitly mapped will just contribute to allStaff
          }
        })
      }

      // count other staff (custom entered staff by users) if present
      if (staff.otherStaff && Array.isArray(staff.otherStaff)) {
        stats.otherStaff = staff.otherStaff.length
        stats.allStaff += stats.otherStaff
      }
    }

    return stats
  } catch (error) {
    console.error('Error fetching personnel statistics:', error)
    throw error
  }
}

export interface TeamReportData {
  role: string
  rider: string
  horse: string
  name: string
  phone: string
  email: string
  paperworkStatus: string
  teamMemberId?: string
  riderId?: string
}

interface PaperworkStatuses {
  universalOrganizerWaver: string
  USEFWaiverAndReleaseOfLiability: string
  USEFEntryAgreement: string
  USDFWaiverAndReleaseOfLiability: string
  USEAWaiverAndReleaseOfLiability: string
  USHJAWaiverAndReleaseOfLiability: string
  [key: string]: string
}

/**
 * Fetches and processes team member data for the Team Report
 *
 * @param eventId The unique identifier of the event
 * @returns Processed team member data for the report
 * @throws {Error} If there's a problem fetching data from Firestore
 */
export const getTeamReportData = async (eventId: string): Promise<TeamReportData[]> => {
  try {
    // get all team members for the event
    const teamMembersSnap = await FirestoreService.filterItems(COLLECTIONS.RIDER_TEAM_MEMBER.NAME, [
      where(COLLECTIONS.RIDER_TEAM_MEMBER.FIELDS.EVENT_ID.KEY, '==', eventId),
    ])

    const reportData = teamMembersSnap.docs.map((doc) => {
      const memberData = doc.data()

      const riderName = memberData.riderName || ''

      const paperworkStatuses = getPaperworkStatuses(memberData.mailLog || [])
      const consolidatedPaperworkStatus = getConsolidatedPaperworkStatus(paperworkStatuses)

      // Return the data in the format needed for the report
      return {
        role: memberData.teamMemberRole || '',
        rider: riderName,
        horse: memberData.horseName || '',
        name: memberData.teamMemberName || '',
        phone: memberData.teamMemberPhoneNumber || memberData.riderPhoneNumber || '',
        email: memberData.teamMemberEmail || memberData.riderEmail || '',
        paperworkStatus: consolidatedPaperworkStatus,
        teamMemberId: memberData.teamMemberId || '',
        riderId: memberData.riderId || '',
      }
    })

    return reportData
  } catch (error) {
    console.error('Error fetching team report data:', error)
    throw error
  }
}

// helper function to normalize status to proper case
const normalizeStatus = (status: string): string => {
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
}

// helper function for paperwork statuses
const getPaperworkStatuses = (
  mailLog: Array<{ paperworkKey: string; status: string }> = []
): PaperworkStatuses => {
  const paperworkStatuses: PaperworkStatuses = {
    universalOrganizerWaver: 'Not sent',
    USEFWaiverAndReleaseOfLiability: 'Not sent',
    USEFEntryAgreement: 'Not sent',
    USDFWaiverAndReleaseOfLiability: 'Not sent',
    USEAWaiverAndReleaseOfLiability: 'Not sent',
    USHJAWaiverAndReleaseOfLiability: 'Not sent',
  }

  // update statuses based on mailLog
  if (mailLog && mailLog.length > 0) {
    mailLog.forEach((log) => {
      if (log.status !== 'Removed') {
        paperworkStatuses[log.paperworkKey] = log.status
      }
    })
  }

  return paperworkStatuses
}

// helper function to consolidate paperwork statuses into a single string
const getConsolidatedPaperworkStatus = (paperworkStatuses: PaperworkStatuses): string => {
  // paperwork name mapping for readability
  const readableNames: { [key: string]: string } = {
    universalOrganizerWaver: 'Universal Organizer Waiver',
    USEFWaiverAndReleaseOfLiability: 'USEF Waiver',
    USEFEntryAgreement: 'USEF Entry Agreement',
    USDFWaiverAndReleaseOfLiability: 'USDF Waiver',
    USEAWaiverAndReleaseOfLiability: 'USEA Waiver',
    USHJAWaiverAndReleaseOfLiability: 'USHJA Waiver',
  }

  // filter out all not sent statuses
  const relevantStatuses = Object.entries(paperworkStatuses)
    .filter(([_, status]) => status !== 'Not sent')
    .map(([key, status]) => `${readableNames[key] || key}: ${normalizeStatus(status)}`)

  // if everything not sent, don't include paperwork status data
  if (relevantStatuses.length === 0) {
    return ''
  }

  return relevantStatuses.join(', ')
}

/**
 * Fetches registration fees for a specific event
 *
 * This function queries the Firestore database to retrieve fee registrations
 * for a specific event, which are used to generate the stall report.
 *
 * @param {string} eventId - Unique identifier of the event
 * @returns {Promise<IRegistrationFeesInterface[]>} A promise that resolves to an array of fee registrations
 * @throws {Error} If there's a problem fetching data from Firestore
 *
 * @example
 * Get fee registrations for an event
 * const fees = await getRegistrationFees('somePegasusEvent');
 */
export const getRegistrationFees = async (
  eventId: string
): Promise<IRegistrationFeesInterface[]> => {
  try {
    const feesSnap = await FirestoreService.filterItems(COLLECTIONS.REGISTRATION_FEES.NAME, [
      where(COLLECTIONS.REGISTRATION_FEES.FIELDS.EVENT_ID.KEY, '==', eventId),
    ])

    const fees = feesSnap.docs.map((doc) => RegistrationFeesModel.fromFirestoreDoc(doc).toObject())

    return fees
  } catch (error) {
    console.error('Error fetching registration fees:', error)
    throw error
  }
}

const RIDER_REPORT_ROLES = ['Guardian', 'Owner', 'Trainer', 'Coach']

/**
 * Fetches rider team member data for specific roles
 *
 * @param eventId The unique identifier of the event
 * @returns A map of rider IDs to their team members, grouped by role (Guardian, Owner, Trainer, Coach)
 * @throws {Error} If there's a problem fetching data from Firestore
 */
export const getRiderTeamMemberData = async (
  eventId: string
): Promise<Map<string, Map<string, IRiderTeamMemberInterface[]>>> => {
  try {
    const teamMembersSnap = await FirestoreService.filterItems(COLLECTIONS.RIDER_TEAM_MEMBER.NAME, [
      where(COLLECTIONS.RIDER_TEAM_MEMBER.FIELDS.EVENT_ID.KEY, '==', eventId),
    ])

    //  map team members by rider ID and role
    const riderTeamMembersMap = new Map<string, Map<string, IRiderTeamMemberInterface[]>>()

    teamMembersSnap.forEach((doc) => {
      const teamMember = RiderTeamMemberModel.fromFirestoreDoc(doc)

      const riderId = teamMember.riderId
      const role = teamMember.teamMemberRole

      if (!riderId || !role || !RIDER_REPORT_ROLES.includes(role)) return

      if (!riderTeamMembersMap.has(riderId)) {
        riderTeamMembersMap.set(riderId, new Map())
      }

      const roleMap = riderTeamMembersMap.get(riderId)!
      if (!roleMap.has(role)) {
        roleMap.set(role, [])
      }

      // add team member to the correct role arr
      roleMap.get(role)!.push(teamMember)
    })

    return riderTeamMembersMap
  } catch (error) {
    console.error('Error fetching team report data:', error)
    throw error
  }
}

type TeamMemberRoleOptions = 'Owner' | 'Guardian' | 'Trainer'
/**
 * Fetches team member ids by specified role
 *
 * @param eventId The unique identifier of the event
 * @param role The team member role to filter by (Owner, Guardian, or Trainer)
 * @returns An array of unique ids for the specified role pertaining to the event
 * @throws {Error} If there's a problem fetching data from Firestore
 */
export const getRoleIds = async (
  eventId: string,
  role: TeamMemberRoleOptions
): Promise<string[]> => {
  try {
    const roleSnaps = await FirestoreService.filterItems(COLLECTIONS.RIDER_TEAM_MEMBER.NAME, [
      where('eventId', '==', eventId),
      where('teamMemberRole', '==', role),
    ])

    const roleIds = new Set<string>()

    roleSnaps.forEach((doc) => {
      const data = doc.data()
      if (data.teamMemberId) {
        roleIds.add(data.teamMemberId)
      }
    })

    return Array.from(roleIds)
  } catch (error) {
    console.error('Error fetching role IDs:', error)
    throw error
  }
}

/**
 * Fetches trainer-rider relationships for an event
 *
 * @param eventId The unique identifier of the event
 * @returns A Map where keys are trainer IDs and values are arrays of objects containing rider IDs and names
 * @throws {Error} If there's a problem fetching data from Firestore
 */
export const getTrainerRiderRelationships = async (
  eventId: string
): Promise<Map<string, Array<{ riderId: string; riderName: string }>>> => {
  try {
    const roleSnaps = await FirestoreService.filterItems(COLLECTIONS.RIDER_TEAM_MEMBER.NAME, [
      where('eventId', '==', eventId),
      where('teamMemberRole', '==', 'Trainer'),
    ])

    // map trainers to their riders
    const trainerRidersMap = new Map<string, Array<{ riderId: string; riderName: string }>>()

    roleSnaps.forEach((doc) => {
      const teamMember = RiderTeamMemberModel.fromFirestoreDoc(doc)

      const trainerId = teamMember.teamMemberId
      const riderId = teamMember.riderId
      const riderName = teamMember.riderName || ''

      if (!trainerId || !riderId) return

      if (!trainerRidersMap.has(trainerId)) {
        trainerRidersMap.set(trainerId, [])
      }

      // check if this rider already in the list for this trainer
      const riders = trainerRidersMap.get(trainerId)!
      const existingRider = riders.find((r) => r.riderId === riderId)

      if (!existingRider) {
        riders.push({ riderId, riderName })
      }
    })

    return trainerRidersMap
  } catch (error) {
    console.error('Error fetching trainer-rider relationships:', error)
    throw error
  }
}

/**
 * Gets a map of guardians to their associated riders
 *
 * @param eventId The ID of the event
 * @returns A map where keys are guardian IDs and values are arrays of rider IDs
 * @throws {Error} If there's a problem fetching data from Firestore
 */
export const getGuardianToRidersMap = async (eventId: string): Promise<Map<string, string[]>> => {
  try {
    const guardianTeamMembers = await FirestoreService.filterItems(
      COLLECTIONS.RIDER_TEAM_MEMBER.NAME,
      [where('eventId', '==', eventId), where('teamMemberRole', '==', 'Guardian')]
    )

    // map of guardian IDs to their associated riders
    const guardianToRidersMap = new Map<string, string[]>()

    guardianTeamMembers.forEach((doc) => {
      const data = doc.data()
      const guardianId = data.teamMemberId
      const riderId = data.riderId

      if (!guardianId || !riderId) return

      if (!guardianToRidersMap.has(guardianId)) {
        guardianToRidersMap.set(guardianId, [])
      }

      const riders = guardianToRidersMap.get(guardianId)!
      if (!riders.includes(riderId)) {
        riders.push(riderId)
      }
    })

    return guardianToRidersMap
  } catch (error) {
    console.error('Error fetching guardian data:', error)
    throw error
  }
}

/**
 * Fetches event staff data for a specific event
 *
 * This function queries the Firestore database to retrieve staff information
 * for a specific event, which is used to generate the staff report.
 *
 * @param {string} eventId - Unique identifier of the event
 * @returns {Promise<any>} A promise that resolves to the event staff data
 * @throws {Error} If there's a problem fetching data from Firestore
 */

export const getStaffRolesData = async (
  eventId: string
): Promise<
  Array<{
    staffId: string | null
    staffRole: string | null
    isScratched: boolean
  }>
> => {
  try {
    if (!eventId) {
      console.error('No event ID provided for getStaffRolesData')
      return []
    }

    const staffSnap = await FirestoreService.getItem(COLLECTIONS.EVENT_STAFF.NAME, eventId)

    if (!staffSnap.exists()) {
      console.error(`Could not find event staff for event ${eventId}`)
      return []
    }

    const eventStaffDoc = EventStaffModel.fromFirestoreDoc(staffSnap).toObject()

    const staffRolesData: Array<{
      staffId: string | null
      staffRole: string | null
      isScratched: boolean
    }> = []

    const { eventStaff = [], otherStaff = [] } = eventStaffDoc

    eventStaff.forEach((currStaff) => {
      currStaff.value?.forEach((currStaffId) => {
        if (currStaffId) {
          staffRolesData.push({
            staffId: currStaffId,
            staffRole: currStaff.title,
            isScratched: !!currStaff?.scratchedValues?.find(
              (currScratchedValue) => currScratchedValue === currStaffId
            ),
          })
        }
      })
    })

    otherStaff.forEach((currOtherStaff) => {
      currOtherStaff.value?.forEach((currOtherStaffId) => {
        if (currOtherStaffId) {
          staffRolesData.push({
            staffId: currOtherStaffId,
            staffRole: currOtherStaff.title,
            isScratched: !!currOtherStaff?.scratchedValues?.find(
              (currScratchedValue) => currScratchedValue === currOtherStaffId
            ),
          })
        }
      })
    })

    return staffRolesData
  } catch (error) {
    console.error('Error fetching staff roles data:', error)
    return []
  }
}

/**
 * Fetches horse paperwork data from the v01_horse_competition_paperwork collection
 * for a specific set of horse IDs
 *
 * @param {string[]} horseIds - Array of horse IDs to fetch paperwork for
 * @returns {Promise<Map<string, HorseCompetitonPaperworkModel>>} A promise that resolves to a map of horse IDs to their paperwork model
 * @throws {Error} If there's a problem fetching data from Firestore
 */
export const getHorsePaperworkData = async (
  horseIds: string[]
): Promise<Map<string, HorseCompetitonPaperworkModel>> => {
  try {
    if (!horseIds || horseIds.length === 0) {
      return new Map()
    }

    const paperworkMap = new Map<string, HorseCompetitonPaperworkModel>()

    // firestore limit of 30 values for 'in' queries
    const batchSize = 30

    for (let i = 0; i < horseIds.length; i += batchSize) {
      const batch = horseIds.slice(i, i + batchSize)

      const paperworkSnap = await FirestoreService.filterItems(
        COLLECTIONS.HORSE_COMPETITION_PAPERWORK.NAME,
        [where('horseId', 'in', batch)]
      )

      paperworkSnap.forEach((doc) => {
        const paperwork = HorseCompetitonPaperworkModel.fromFirestoreDoc(doc)
        if (paperwork.horseId) {
          paperworkMap.set(paperwork.horseId, paperwork)
        }
      })
    }

    return paperworkMap
  } catch (error) {
    console.error('Error fetching horse paperwork data:', error)
    throw error
  }
}
